// src/pages/analytics/payables/VendorSpendTab.tsx
//
// Vendor spend (recipe vendor-spend, agent 3.24.0+): bill totals per vendor by
// month / quarter / year for any range, against the same range a year earlier.
// Click a vendor for its items (qty, avg cost vs last year) and bills.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { RefreshCw, Search, ShoppingCart, X } from 'lucide-react';
import { apiV2 as api } from '../../../services/apiV2';
import DateField from '../../../components/DateField';
import type { VendorSpendItem, VendorSpendVendor } from '../../../types/payables';
import { exportCsv, type CsvColumns } from '../../../lib/csvExport';
import { exportXlsx } from '../../../lib/xlsxExport';
import {
  ChangePct, ExportButtons, LoadError, Spinner, Tile, controlsCls, fmtCost, fmtCur, fmtCur2, fmtNum,
  isoDaysAgo, isoYearStart, labelCls, pill, tableWrap, tdCls, tdR, thCls, thR,
} from './apShared';

const PERIODS = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

const tooltipProps = {
  cursor: { fill: '#475569', fillOpacity: 0.15 },
  contentStyle: { backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' },
  labelStyle: { color: '#f1f5f9' },
};

const ITEM_COLS: CsvColumns<VendorSpendItem> = [
  ['Item', r => r.item], ['Description', r => r.desc], ['Spend', r => r.amount], ['Qty', r => r.qty],
  ['Avg cost', r => r.avgCost ?? ''], ['Prior spend', r => r.priorAmount], ['Prior qty', r => r.priorQty],
  ['Prior avg cost', r => r.priorAvgCost ?? ''], ['Avg cost change %', r => r.avgCostChangePct ?? ''],
];

export function VendorSpendTab() {
  const [dateFrom, setDateFrom] = useState(isoYearStart);
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [period, setPeriod] = useState('month');
  const [search, setSearch] = useState('');
  const [vendor, setVendor] = useState<VendorSpendVendor | null>(null);

  const q = useQuery({
    queryKey: ['vendorSpend', dateFrom, dateTo, period],
    queryFn: () => api.getVendorSpend({ dateFrom, dateTo, period }),
    staleTime: 60_000,
    retry: 0,
  });
  const dq = useQuery({
    queryKey: ['vendorSpend', 'vendor', vendor?.code, dateFrom, dateTo, period],
    queryFn: () => api.getVendorSpend({ dateFrom, dateTo, period, vendor: vendor!.code }),
    enabled: !!vendor,
    staleTime: 60_000,
  });
  const data = q.data;

  const vendors = useMemo(() => {
    const s = search.trim().toLowerCase();
    const rows = data?.vendors ?? [];
    return s ? rows.filter(v => v.name.toLowerCase().includes(s) || String(v.code).includes(s)) : rows;
  }, [data, search]);

  const chartSrc = vendor && dq.data ? dq.data.totals : data?.totals;
  const chartData = (data?.periods ?? []).map(p => ({
    period: p,
    'This period': chartSrc?.byPeriod[p] ?? 0,
    'Year before': chartSrc?.priorByPeriod[p] ?? 0,
  }));

  const vendorCols: CsvColumns<VendorSpendVendor> = [
    ['Code', r => r.code], ['Vendor', r => r.name], ['Spend', r => r.total], ['Prior year', r => r.prior],
    ['Change %', r => r.changePct ?? ''], ['Share %', r => r.share], ['Bills', r => r.bills],
    ...(data?.periods ?? []).map(p => [p, (r: VendorSpendVendor) => r.byPeriod[p] ?? 0] as [string, (r: VendorSpendVendor) => number]),
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><ShoppingCart size={20} /> Vendor Spend</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Billed by vendor {data && <span className="text-slate-500">· {data.from} → {data.to} vs {data.priorFrom} → {data.priorTo}</span>}
        </p>
      </div>

      <div className={controlsCls}>
        <DateField label="From" value={dateFrom} onChange={setDateFrom} />
        <DateField label="To" value={dateTo} onChange={setDateTo} />
        <div className="flex flex-col gap-1">
          <span className={labelCls}>By</span>
          <div className="flex gap-1">
            {PERIODS.map(p => <button key={p.value} className={pill(period === p.value)} onClick={() => setPeriod(p.value)}>{p.label}</button>)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Vendor</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor…"
              className="pl-6 pr-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-48 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <div className="flex-1" />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && <LoadError error={q.error} what="vendor spend" />}

      {q.isLoading ? <Spinner /> : !q.error && data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile tone="violet" label="Spend" value={fmtCur(data.totals.total)} sub={`${data.totals.vendors} vendors`} />
            <Tile label="Year before" value={fmtCur(data.totals.prior)} />
            <Tile label="Change" value={<ChangePct pct={data.totals.changePct} invert />} />
            <Tile label="Top vendor share" value={vendors[0] ? `${vendors[0].share.toFixed(1)}%` : '–'} sub={vendors[0]?.name} />
          </div>

          <div className="rounded-xl border border-slate-700/40 bg-slate-900/30 p-3">
            <div className="text-xs text-slate-400 mb-2 flex items-center gap-2">
              {vendor ? <>Spend — {vendor.name} <button onClick={() => setVendor(null)} className="text-slate-500 hover:text-white"><X size={12} /></button></> : 'Spend — all vendors'}
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                  <XAxis dataKey="period" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                    tickFormatter={(v: number) => v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`} />
                  <Tooltip {...tooltipProps} formatter={(v) => fmtCur(Number(v ?? 0))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
                  <Bar dataKey="Year before" fill="#64748b" fillOpacity={0.6} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="This period" fill="#8b5cf6" fillOpacity={0.8} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">By vendor <span className="text-slate-500 font-normal">({vendors.length}) · click for items</span></h3>
              <div className="flex gap-1">
                <ExportButtons what="vendor spend" disabled={!vendors.length}
                  onCsv={() => exportCsv('vendor-spend', vendorCols, vendors)} onXlsx={() => exportXlsx('vendor-spend', vendorCols, vendors)} />
              </div>
            </div>
            <div className={`${tableWrap} max-h-[460px]`}>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-900">
                  <tr>
                    <th className={thCls}>Vendor</th><th className={thR}>Spend</th><th className={thR}>Year before</th>
                    <th className={thR}>Change</th><th className={thR}>Share</th><th className={thR}>Bills</th>
                    {data.periods.map(p => <th key={p} className={thR}>{p}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {vendors.map(v => (
                    <tr key={v.code} onClick={() => setVendor(vendor?.code === v.code ? null : v)}
                      className={`border-t border-slate-800/60 cursor-pointer hover:bg-slate-800/40 ${vendor?.code === v.code ? 'bg-blue-500/10' : ''}`}>
                      <td className={tdCls}><span className="text-slate-500 mr-1.5">{v.code}</span>{v.name}</td>
                      <td className={`${tdR} font-medium`}>{fmtCur(v.total)}</td>
                      <td className={`${tdR} text-slate-400`}>{fmtCur(v.prior)}</td>
                      <td className={tdR}><ChangePct pct={v.changePct} invert /></td>
                      <td className={tdR}>{v.share.toFixed(1)}%</td>
                      <td className={tdR}>{v.bills}</td>
                      {data.periods.map(p => <td key={p} className={`${tdR} ${(v.byPeriod[p] ?? 0) === 0 ? 'text-slate-600' : ''}`}>{(v.byPeriod[p] ?? 0) === 0 ? '–' : fmtCur(v.byPeriod[p])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {vendor && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-200">Items — {vendor.name}</h3>
                <div className="flex gap-1">
                  <ExportButtons what="items" disabled={!dq.data?.items?.length}
                    onCsv={() => exportCsv(`vendor-${vendor.code}-items`, ITEM_COLS, dq.data?.items ?? [])}
                    onXlsx={() => exportXlsx(`vendor-${vendor.code}-items`, ITEM_COLS, dq.data?.items ?? [])} />
                </div>
              </div>
              {dq.isLoading ? <Spinner /> : dq.error ? <LoadError error={dq.error} what="the vendor's items" /> : (
                <div className="grid lg:grid-cols-3 gap-3">
                  <div className={`${tableWrap} max-h-[420px] lg:col-span-2`}>
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-slate-900">
                        <tr>
                          <th className={thCls}>Item</th><th className={thR}>Spend</th><th className={thR}>Qty</th>
                          <th className={thR}>Avg cost</th><th className={thR}>Year before</th><th className={thR}>Cost change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dq.data?.items ?? []).map(i => (
                          <tr key={i.item} className="border-t border-slate-800/60">
                            <td className={tdCls}>{i.item} <span className="text-slate-500">{i.desc}</span></td>
                            <td className={tdR}>{fmtCur2(i.amount)}</td>
                            <td className={tdR}>{fmtNum(i.qty)}</td>
                            <td className={tdR}>{i.avgCost != null ? fmtCost(i.avgCost) : '–'}</td>
                            <td className={`${tdR} text-slate-400`}>{i.priorAvgCost != null ? fmtCost(i.priorAvgCost) : '–'}</td>
                            <td className={tdR}><ChangePct pct={i.avgCostChangePct} invert /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className={`${tableWrap} max-h-[420px]`}>
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-slate-900">
                        <tr><th className={thCls}>PO</th><th className={thCls}>Date</th><th className={thR}>Total</th><th className={thCls}>Received</th><th className={thCls}>QB</th></tr>
                      </thead>
                      <tbody>
                        {(dq.data?.bills ?? []).map(b => (
                          <tr key={b.doc} className="border-t border-slate-800/60">
                            <td className={tdCls}>{b.doc}{b.ref && <span className="text-slate-500"> · {b.ref}</span>}</td>
                            <td className={tdCls}>{b.date}</td>
                            <td className={tdR}>{fmtCur2(b.total)}</td>
                            <td className={tdCls}>{b.received ? <span className="text-emerald-400">Yes</span> : <span className="text-amber-300">Not yet</span>}</td>
                            <td className={tdCls}>{b.sentToQb == null ? '–' : b.sentToQb ? <span className="text-emerald-400">Sent</span> : <span className="text-amber-300">Not sent</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
